import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import { getFileUploadTranslations } from '@common/i18n/fileUpload';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import {
    Box,
    CircularProgress,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useQueries } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { fileNameMap } from '@/constants';
import { deleteFile, getFileDetails, uploadFile } from '@/services/FileService';
import useIdentityStore from '@/stores/useIdentityStore';
import { truncate } from '@/utils';
import { errorSnackbar } from '@/utils/snackbar';

interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
    isLoading: boolean;
    error?: string;
}

interface Props {
    name: string;
    tabindex?: string | number;
    uploadButtonLabel?: string;
    language?: TemplateFormLanguage;
    validationSchema: {
        fileSize?: number;
        fileType?: string[];
        fileCount?: number;
    };
    fileIds?: string[];
    onFileIdsChange?: (fileIds: string[]) => void;
    disabled?: boolean;
    isError?: boolean;
    typeOfIdValue?: any;
    typeofDocument?: string;
    selectedLawFirmId?: number | string;
    deleteOnRemove?: boolean;
}

export default function FormUploadField({
    name,
    tabindex,
    uploadButtonLabel,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    fileIds,
    disabled,
    onFileIdsChange,
    typeOfIdValue,
    validationSchema,
    isError,
    typeofDocument,
    selectedLawFirmId,
    deleteOnRemove = false,
}: Props) {
    const t = getFileUploadTranslations(language);
    const resolvedUploadButtonLabel = uploadButtonLabel ?? t.uploadFile;
    const uploadRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [deletingFileIds, setDeletingFileIds] = useState<Set<string>>(new Set());
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const loggedInUser = useIdentityStore((state) => state.user);
    const isDeleting = deletingFileIds.size > 0;
    const isInteractionDisabled = disabled || isUploading || isDeleting;

    const fileQueries = useQueries({
        queries: (fileIds ?? []).map((fileId) => ({
            queryKey: ['fileDetails', fileId],
            queryFn: async () => {
                const response = await getFileDetails(fileId);
                return response.data;
            },
            staleTime: 5 * 60_000,
            enabled: !!fileId,
        })),
    });

    const uploadedFiles: UploadedFile[] = useMemo(() => {
        return (fileIds ?? []).map((fileId, index) => {
            const query = fileQueries[index];
            if (query?.isLoading) {
                return {
                    id: fileId,
                    name: t.loading,
                    size: 0,
                    type: '',
                    isLoading: true,
                };
            }
            if (query?.error || !query?.data) {
                return {
                    id: fileId,
                    name: t.unknownFile,
                    size: 0,
                    type: '',
                    isLoading: false,
                    error: t.failedToLoadDetails,
                };
            }
            return {
                id: fileId,
                name: query.data.fileName,
                size: 0,
                type: query.data.fileType,
                isLoading: false,
            };
        });
    }, [fileIds, fileQueries, t]);

    const handleSingleFileUpload = async (file: File, name: string, index: number) => {
        if (file.name.length > 400) {
            errorSnackbar(t.fileNameTooLong);
            return;
        }
        const resolvedLawFirmId = selectedLawFirmId || loggedInUser?.lawFirm?.lawFirmId;
        if (resolvedLawFirmId && file) {
            const typeOfDoc = typeofDocument || fileNameMap[name];
            try {
                const fileResp = await uploadFile(
                    file,
                    resolvedLawFirmId,
                    typeOfDoc,
                    typeOfIdValue,
                );
                return { fileId: fileResp.fileId, index: index };
            } catch (error) {
                console.error(error);
                throw error;
            }
        }
    };

    const handleMultipleFileUpload = async (files: FileList, name: string) => {
        setIsUploading(true);
        try {
            const filesResp = await Promise.all(
                Array.from(files).map((file, index) => handleSingleFileUpload(file, name, index)),
            );
            return filesResp;
        } catch (error) {
            console.error(error);
            errorSnackbar(t.failedToUpload);
        } finally {
            setIsUploading(false);
        }
    };

    interface FileToValidate {
        name: string;
        size: number;
        type: string;
    }

    const onValidation = (files: FileToValidate[] | null, existingCount: number) => {
        if (!files || files?.length === 0) {
            errorSnackbar(t.noFilesSelected);
            return false;
        }

        if (files.some((file) => file.name.length > 400)) {
            errorSnackbar(t.fileNameTooLong);
            return false;
        }

        const totalCount = existingCount + files.length;

        if (validationSchema?.fileCount) {
            if (totalCount > validationSchema?.fileCount && validationSchema?.fileCount === 1) {
                errorSnackbar(t.onlyOneFile);
                return false;
            } else if (totalCount > validationSchema?.fileCount) {
                errorSnackbar(t.onlyNFilesAtATime(validationSchema.fileCount));
                return false;
            }
        } else if (totalCount > 1) {
            errorSnackbar(t.onlyOneFileAtATime);
            return false;
        }

        if (validationSchema.fileType && validationSchema?.fileType?.length > 0) {
            if (files?.some((file) => !validationSchema.fileType?.includes(file?.type))) {
                if (typeofDocument === 'Retainer Agreement') {
                    errorSnackbar(t.invalidFileTypeRetainer);
                } else {
                    errorSnackbar(t.invalidFileTypeIdDocuments);
                }
                return false;
            }
        }

        if (
            validationSchema.fileSize &&
            files?.some((file) => file?.size > (validationSchema.fileSize as number))
        ) {
            errorSnackbar(t.fileSizeMustBeLessThan(validationSchema.fileSize / (1024 * 1024)));
            return false;
        }

        return true;
    };

    const onFileUpload = async (files: FileList | null) => {
        if (isInteractionDisabled) return;
        if (files) {
            const filesToValidate = Array.from(files).map((file) => ({
                name: file.name,
                size: file.size,
                type: file.type,
            }));
            const existingCount = fileIds?.length ?? 0;
            const isValid = onValidation(filesToValidate, existingCount);
            if (!isValid) {
                if (uploadRef.current?.value) {
                    uploadRef.current.value = '';
                }
                return;
            }
            const filesResp = await handleMultipleFileUpload(files, name);
            if (filesResp && onFileIdsChange) {
                const newFileIds = filesResp
                    .filter((f): f is { fileId: string; index: number } => !!f?.fileId)
                    .map((f) => f.fileId);

                onFileIdsChange([...(fileIds ?? []), ...newFileIds]);

                if (uploadRef.current?.value) {
                    uploadRef.current.value = '';
                }
            }
        }
    };

    const onRemoveFile = async (fileId: string) => {
        if (isInteractionDisabled) return;
        const updatedIds = (fileIds ?? []).filter((id) => id !== fileId);
        if (!deleteOnRemove) {
            onFileIdsChange?.(updatedIds);
            if (uploadRef.current?.value) {
                uploadRef.current.value = '';
            }
            return;
        }

        setDeletingFileIds((prev) => new Set(prev).add(fileId));
        try {
            await deleteFile(fileId);
            onFileIdsChange?.(updatedIds);

            if (uploadRef.current?.value) {
                uploadRef.current.value = '';
            }
        } catch (error) {
            console.error(error);
            errorSnackbar(t.failedToDelete);
        } finally {
            setDeletingFileIds((prev) => {
                const next = new Set(prev);
                next.delete(fileId);
                return next;
            });
        }
    };

    const dropZoneBorderColor = isError
        ? theme.palette.error.main
        : isDragOver
          ? theme.palette.primary.main
          : theme.palette.neutral.N_300;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '16px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                boxSizing: 'border-box',
                padding: '16px',
                border: '2px dashed',
                borderColor: dropZoneBorderColor,
                borderRadius: '6px',
                backgroundColor: isDragOver ? '#f8fbff' : '#fafbfc',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                ...(!isInteractionDisabled && {
                    '&:hover': {
                        borderColor: isError
                            ? theme.palette.error.main
                            : theme.palette.primary.main,
                        backgroundColor: '#f8fbff',
                    },
                }),
            }}
            role="region"
            aria-label={t.ariaFileUpload}
            onDrop={(event: React.DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
                setIsDragOver(false);
                if (isInteractionDisabled) return;
                onFileUpload(event.dataTransfer?.files);
            }}
            onDragOver={(event: React.DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (isInteractionDisabled) return;
                if (!isDragOver) setIsDragOver(true);
            }}
            onDragEnter={(event: React.DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (isInteractionDisabled) return;
                setIsDragOver(true);
            }}
            onDragLeave={(event: React.DragEvent<HTMLDivElement>) => {
                event.preventDefault();
                event.stopPropagation();
                const relatedTarget = event.relatedTarget as Node | null;
                if (!event.currentTarget.contains(relatedTarget)) {
                    setIsDragOver(false);
                }
            }}
        >
            {uploadedFiles.length > 0 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                    }}
                >
                    {uploadedFiles.map((file) => {
                        const isDeleting = deletingFileIds.has(file.id);
                        const showSpinner = file.isLoading || isDeleting;

                        return (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 16px',
                                    border: `1px solid ${file.error ? theme.palette.error.main : theme.palette.neutral.N_300}`,
                                    borderRadius: '4px',
                                    width: 'fit-content',
                                    color: theme.palette.neutral.black,
                                    opacity: isDeleting ? 0.5 : 1,
                                }}
                                key={file.id}
                            >
                                {showSpinner ? (
                                    <CircularProgress size={16} />
                                ) : file.type?.includes('image') ? (
                                    <ImageIcon sx={{ fontSize: 14 }} />
                                ) : (
                                    <DescriptionIcon sx={{ fontSize: 14 }} />
                                )}
                                <Typography
                                    variant="h4"
                                    sx={{
                                        fontWeight: 500,
                                        color: file.error
                                            ? theme.palette.error.main
                                            : theme.palette.neutral.black,
                                        lineHeight: '16px',
                                    }}
                                >
                                    {isMobile ? truncate(file.name, 18) : file.name}
                                </Typography>
                                <IconButton
                                    sx={{
                                        padding: '0px',
                                        color: theme.palette.neutral.black,
                                        fontSize: 'medium',
                                    }}
                                    onClick={() => onRemoveFile(file.id)}
                                    disabled={isInteractionDisabled || file.isLoading}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        );
                    })}
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap',
                }}
            >
                <input
                    type="file"
                    aria-label={t.ariaChooseFile}
                    multiple={true}
                    ref={uploadRef}
                    style={{ display: 'none' }}
                    disabled={isInteractionDisabled}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (event.target.files) {
                            onFileUpload(event.target.files);
                        }
                    }}
                />
                <LoadingButton
                    tabIndex={tabindex === undefined ? undefined : Number(tabindex)}
                    sx={{
                        padding: '12px 24px',
                        textTransform: 'none',
                        marginTop: '0px',
                        whiteSpace: 'nowrap',
                        color: isError ? theme.palette.error.main : theme.palette.primary.main,
                        '& .MuiButton-startIcon > *:nth-of-type(1)': {
                            fontSize: 16,
                        },
                    }}
                    variant="outlined"
                    loading={isUploading}
                    color={isError ? 'error' : 'primary'}
                    startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                    disabled={
                        isInteractionDisabled ||
                        (validationSchema.fileCount
                            ? (fileIds?.length ?? 0) >= validationSchema.fileCount
                            : (fileIds?.length ?? 0) >= 1)
                    }
                    onClick={() => uploadRef.current?.click()}
                >
                    {resolvedUploadButtonLabel}
                </LoadingButton>
                <Typography variant="body2" sx={{ color: theme.palette.neutral.N_500 }}>
                    {t.dragAndDropHint}
                </Typography>
            </Box>
        </Box>
    );
}
